<template>
  <div id="projects" class="projects-page">
    <div class="mobile-top-actions">
      <router-link
        v-if="user && user.can_create_project && isOnline"
        :to="{ name: 'project_create', params: { action: 'create' } }"
        class="new-project-btn mobile-only"
        data-test="create-project-mobile"
      >
        <i class="plus icon" aria-hidden="true" />
        Nouveau projet
      </router-link>
      <button
        type="button"
        class="mobile-filters-toggle"
        :aria-expanded="sidebarOpen"
        @click="sidebarOpen = !sidebarOpen"
      >
        <i class="fas fa-sliders-h" aria-hidden="true" />
        Filtres
      </button>
    </div>

    <div
      v-if="sidebarOpen"
      class="sidebar-backdrop"
      @click="sidebarOpen = false"
    />

    <div class="projects-layout">
      <aside :class="['projects-sidebar', { open: sidebarOpen }]">
        <router-link
          v-if="user && user.can_create_project && isOnline"
          :to="{ name: 'project_create', params: { action: 'create' } }"
          class="new-project-btn"
          data-test="create-project"
        >
          <i class="plus icon" aria-hidden="true" />
          Nouveau projet
        </router-link>

        <div class="sidebar-panel">
          <ProjectsMenu
            :loading="loading"
            @filter="setProjectsFilters"
            @getData="getData"
            @loading="setLoader"
          />
        </div>
      </aside>

      <section class="projects-main">
        <div class="list-toolbar">
          <p class="results-count">
            <template v-if="count">
              {{ resultsRange }} sur {{ count }} résultat<span v-if="count > 1"
                >s</span
              >
            </template>
            <template v-else> Aucun résultat </template>
          </p>

          <div class="toolbar-center">
            <Pagination
              v-if="count"
              :nb-pages="nbPages"
              @page-update="changePage"
            />
            <label class="toolbar-field page-size-field">
              <span>Résultats par page</span>
              <select
                :value="pageSize"
                :disabled="loading"
                aria-label="Résultats par page"
                @change="changePageSize"
              >
                <option
                  v-for="size in pageSizeOptions"
                  :key="size"
                  :value="size"
                >
                  {{ size }}
                </option>
              </select>
            </label>
          </div>
        </div>

        <div
          id="projects-results"
          ref="resultsList"
          :class="['projects-list', 'dimmable', { dimmed: loading }]"
          data-test="project-list"
        >
          <div :class="['ui inverted dimmer', { active: loading }]">
            <div class="ui loader" />
          </div>

          <ProjectsListItem
            v-for="project in visibleProjects"
            :key="project.slug"
            :project="project"
            @open-modal="openProjectModal"
          />

          <p v-if="!visibleProjects.length" class="empty-state">
            Vous n'avez accès à aucun projet.
          </p>
        </div>
      </section>
    </div>

    <ProjectModal :is-subscriber="isSubscriber" @action="handleModalAction" />
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from "vuex";

import projectAPI from "@/services/project-api";
import ProjectsMenu from "@/components/Projects/ProjectsMenu";
import ProjectsListItem from "@/components/Projects/ProjectsListItem";
import ProjectModal from "@/components/Project/Detail/ProjectModal";
import Pagination from "@/components/Pagination";

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];
const ROUTE_KEYS = ["page", "page_size", "search", "ordering"];

export default {
  name: "ProjectsList",

  components: {
    ProjectsMenu,
    ProjectsListItem,
    ProjectModal,
    Pagination,
  },

  data() {
    return {
      loading: false,
      sidebarOpen: false,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      actionProject: null,
      isSubscriber: false,
      updatingRoute: false,
    };
  },

  computed: {
    ...mapState(["configuration", "user", "isOnline"]),
    ...mapState("projects", [
      "projects",
      "count",
      "filters",
      "currentPage",
      "pageSize",
      "ordering",
      "searchProjectsFilter",
    ]),
    ...mapGetters("projects", ["nbPages"]),
    DJANGO_BASE_URL() {
      return this.$store.state.configuration.VUE_APP_DJANGO_BASE;
    },
    API_BASE_URL() {
      return this.configuration.VUE_APP_DJANGO_API_BASE;
    },
    resultsRange() {
      if (!this.count) return "0";
      const start = (this.currentPage - 1) * this.pageSize + 1;
      const end = Math.min(this.currentPage * this.pageSize, this.count);
      return `${start} - ${end}`;
    },
    visibleProjects() {
      const projects = this.projects || [];
      if (this.user) return projects;
      return projects.filter(
        (project) => project.access_level_pub_feature === "Utilisateur anonyme"
      );
    },
  },

  watch: {
    "$route.query"() {
      if (this.updatingRoute) return;
      if (this.sameQuery(this.$route.query, this.buildQuery())) return;
      this.hydrateFromRoute();
      this.getData();
    },
    currentPage() {
      this.syncRoute();
    },
    pageSize() {
      this.syncRoute();
    },
    ordering() {
      this.syncRoute();
    },
    searchProjectsFilter() {
      this.syncRoute();
    },
    filters: {
      deep: true,
      handler() {
        this.syncRoute();
      },
    },
  },

  created() {
    this.hydrateFromRoute();
    this.$store.commit("projects/SET_PROJECT", null);
  },

  beforeDestroy() {
    this.CLOSE_PROJECT_MODAL();
  },

  methods: {
    ...mapMutations(["DISPLAY_MESSAGE"]),
    ...mapMutations("modals", ["OPEN_PROJECT_MODAL", "CLOSE_PROJECT_MODAL"]),
    ...mapMutations("projects", [
      "SET_CURRENT_PAGE",
      "SET_PAGE_SIZE",
      "SET_ORDERING",
      "SET_PROJECTS_FILTER",
      "SET_PROJECTS_SEARCH_STATE",
    ]),
    ...mapActions("projects", ["GET_PROJECTS"]),

    getData(page) {
      this.loading = true;
      this.GET_PROJECTS({ page })
        .then(() => {
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    buildQuery() {
      const query = {
        page: String(this.currentPage || 1),
      };
      if (this.pageSize && this.pageSize !== 10) {
        query.page_size = String(this.pageSize);
      }
      if (this.searchProjectsFilter) {
        query.search = this.searchProjectsFilter;
      }
      if (this.ordering && this.ordering !== "-created_on") {
        query.ordering = this.ordering;
      }
      Object.entries(this.filters || {}).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          query[key] = String(value);
        }
      });
      return query;
    },

    sameQuery(left, right) {
      const keys = new Set([
        ...Object.keys(left || {}),
        ...Object.keys(right || {}),
      ]);
      for (const key of keys) {
        const a = left && left[key] != null ? String(left[key]) : "";
        const b = right && right[key] != null ? String(right[key]) : "";
        if (a !== b) return false;
      }
      return true;
    },

    syncRoute() {
      const query = this.buildQuery();
      if (this.sameQuery(this.$route.query, query)) return;
      this.updatingRoute = true;
      this.$router
        .replace({ query })
        .catch(() => {})
        .then(() => {
          this.updatingRoute = false;
        });
    },

    hydrateFromRoute() {
      const query = this.$route.query || {};
      const page = Number(query.page);
      this.SET_CURRENT_PAGE(page > 0 ? page : 1);
      const size = Number(query.page_size);
      if (PAGE_SIZE_OPTIONS.includes(size)) {
        this.SET_PAGE_SIZE(size);
      }
      const search = query.search ? String(query.search) : null;
      this.SET_PROJECTS_SEARCH_STATE({
        isSearched: !!search,
        text: search,
      });
      if (query.ordering) {
        this.SET_ORDERING(String(query.ordering));
      }
      const filterKeys = new Set([
        ...Object.keys(this.filters || {}),
        ...Object.keys(query),
      ]);
      filterKeys.forEach((key) => {
        if (ROUTE_KEYS.includes(key)) return;
        this.SET_PROJECTS_FILTER({
          filter: key,
          value: query[key] ? String(query[key]) : null,
        });
      });
    },

    setLoader(e) {
      this.loading = e;
    },

    changePage(page) {
      this.getData(page);
      this.sidebarOpen = false;
      this.$nextTick(() => {
        const list = this.$refs.resultsList;
        if (list) list.scrollTop = 0;
      });
    },

    changePageSize(e) {
      const size = Number(e.target.value);
      if (!size || size === this.pageSize) return;
      this.SET_PAGE_SIZE(size);
      this.SET_CURRENT_PAGE(1);
      this.getData(1);
      this.sidebarOpen = false;
    },

    setProjectsFilters(e, noUpdate) {
      this.SET_PROJECTS_FILTER(e);
      this.SET_CURRENT_PAGE(1);
      if (!noUpdate) {
        this.getData();
        this.sidebarOpen = false;
      }
    },

    openProjectModal({ type, project }) {
      this.actionProject = project;
      if (type === "subscribe") {
        projectAPI
          .getProjectSubscription({
            baseUrl: this.API_BASE_URL,
            projectSlug: project.slug,
          })
          .then((data) => {
            this.isSubscriber = !!(data && data.is_suscriber);
            if (this.actionProject) {
              this.$set(this.actionProject, "is_suscriber", this.isSubscriber);
            }
            this.OPEN_PROJECT_MODAL("subscribe");
          })
          .catch(() => {
            this.actionProject = null;
            this.DISPLAY_MESSAGE({
              comment: "Impossible de récupérer votre abonnement à ce projet.",
              level: "negative",
            });
          });
        return;
      }
      this.OPEN_PROJECT_MODAL(type);
    },

    handleModalAction(e) {
      switch (e) {
        case "subscribe":
          this.subscribeProject();
          break;
        case "deleteProject":
          this.deleteProject();
          break;
      }
    },

    subscribeProject() {
      if (!this.actionProject) return;
      projectAPI
        .subscribeProject({
          baseUrl: this.API_BASE_URL,
          suscribe: !this.isSubscriber,
          projectSlug: this.actionProject.slug,
        })
        .then((data) => {
          this.isSubscriber = !!(data && data.is_suscriber);
          if (this.actionProject) {
            this.$set(this.actionProject, "is_suscriber", this.isSubscriber);
          }
          this.CLOSE_PROJECT_MODAL();
          if (this.isSubscriber) {
            this.DISPLAY_MESSAGE({
              comment:
                "Vous êtes maintenant abonné aux notifications de ce projet.",
              level: "positive",
            });
          } else {
            this.DISPLAY_MESSAGE({
              comment: "Vous ne recevrez plus les notifications de ce projet.",
              level: "negative",
            });
          }
        })
        .catch(() => {
          this.CLOSE_PROJECT_MODAL();
          this.DISPLAY_MESSAGE({
            comment:
              "Une erreur est survenue lors de la modification de votre abonnement.",
            level: "negative",
          });
        });
    },

    deleteProject() {
      if (!this.actionProject) return;
      const projectTitle = this.actionProject.title;
      //* si le projet supprimé était le seul de la page, cette page n'existe plus côté API
      const wasLastOfPage = this.projects.length === 1 && this.currentPage > 1;
      projectAPI
        .deleteProject(this.API_BASE_URL, this.actionProject.slug)
        .then((response) => {
          this.CLOSE_PROJECT_MODAL();
          if (response === "success") {
            const page = wasLastOfPage
              ? this.currentPage - 1
              : this.currentPage;
            this.SET_CURRENT_PAGE(page);
            this.getData(page);
            this.DISPLAY_MESSAGE({
              comment: `Le projet ${projectTitle} a bien été supprimé.`,
              level: "positive",
            });
          } else {
            this.DISPLAY_MESSAGE({
              comment: `Une erreur est survenu lors de la suppression du projet ${projectTitle}.`,
              level: "negative",
            });
          }
        })
        .catch(() => {
          this.CLOSE_PROJECT_MODAL();
          this.DISPLAY_MESSAGE({
            comment: `Une erreur est survenu lors de la suppression du projet ${projectTitle}.`,
            level: "negative",
          });
        });
    },
  },
};
</script>

<style lang="less" scoped>
.projects-page {
  position: fixed;
  top: 55px;
  right: 0;
  bottom: 40px;
  left: 0;
  z-index: 0;
  width: auto;
  max-width: none;
  margin: 0;
  padding: 20px 30px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f3f5f8;
}

.mobile-top-actions {
  display: none;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.mobile-only {
  display: none !important;
}

.mobile-filters-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.7rem 1rem;
  border: 1px solid #d5dbe3;
  border-radius: 4px;
  background: #fff;
  color: var(--header-color, #1f3460);
  font-weight: 600;
  cursor: pointer;

  .icon,
  .fas {
    margin: 0;
  }
}

.sidebar-backdrop {
  display: none;
}

.projects-layout {
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 3fr);
  grid-template-rows: minmax(0, 1fr);
  align-items: stretch;
  gap: 20px;
}

.projects-sidebar {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
  min-width: 0;
  align-self: stretch;
}

.new-project-btn {
  flex: none;
  display: flex;
  align-self: flex-start;
  align-items: center;
  gap: 10px;
  padding: var(--spacing-spacing-around-normal, 10px) var(--spacing-10, 10px);
  border-radius: var(--radius-radius-button, 8px);
  border: var(--spacing-paisseur-leger, 1px) solid var(--surface-surface-high-level, #3A468E);
  background: var(--surface-surface-high-level, #3A468E);
  color: #fff !important;
  font-weight: 700;
  text-decoration: none;
  transition: background 0.15s ease;

  .icon {
    margin: 0;
  }

  &:hover {
    background: #2E3873;
    border-color: #2E3873;
    color: #fff !important;
  }
}

.sidebar-panel {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 0;
  border-radius: 6px;
  padding: 1rem;
  overflow: hidden;
}

.projects-main {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 0;
  border-radius: 6px;
  padding: 1rem 1.25rem 1.25rem;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.12);
  align-self: stretch;
}

.list-toolbar {
  flex: none;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: start;
  column-gap: 1.25rem;
  row-gap: 0.85rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid #e5e9f0;
  margin-bottom: 0.25rem;
}

.results-count {
  margin: 0;
  color: #6b7280;
  font-size: 0.92rem;
  font-weight: 600;
  line-height: 2rem;
  justify-self: start;
}

.toolbar-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.45rem;
  grid-column: 2;
  justify-self: center;

  /deep/ .custom-pagination {
    margin: 0;
  }
}

.toolbar-field {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  margin: 0;
  color: #4b5563;
  font-size: 0.88rem;
  font-weight: 600;
  white-space: nowrap;

  select {
    appearance: none;
    -webkit-appearance: none;
    min-width: 3.25rem;
    height: 2rem;
    padding: 0 1.75rem 0 0.55rem;
    border: 1px solid #d5dbe3;
    border-radius: 3px;
    background-color: #fff;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%234b5563' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.55rem center;
    background-size: 0.55rem;
    color: #1f2a37;
    font: inherit;
    font-weight: 600;
    cursor: pointer;

    &:focus {
      outline: none;
      border-color: var(--header-color, #1f3460);
      box-shadow: 0 0 0 1px rgba(31, 52, 96, 0.2);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

.page-size-field select {
  min-width: 3.5rem;
}

.projects-list {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.projects-list.dimmable.dimmed {
  overflow-x: hidden;
  overflow-y: auto;
}

.empty-state {
  padding: 2rem 0.5rem;
  color: #6b7280;
}

@media screen and (max-width: 980px) {
  .mobile-top-actions {
    display: flex;
  }

  .mobile-only {
    display: inline-flex !important;
  }

  .sidebar-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(17, 24, 39, 0.35);
    z-index: 1002;
  }

  .projects-layout {
    grid-template-columns: 1fr;
  }

  .projects-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1003;
    width: 86vw;
    max-width: 320px;
    height: 100vh;
    overflow-y: auto;
    padding: 1rem;
    background: #f5f7fa;
    transform: translateX(-105%);
    transition: transform 0.2s ease;
    box-shadow: 4px 0 20px rgba(0, 0, 0, 0.12);

    &.open {
      transform: translateX(0);
    }

    @media (max-width: 980px) {
      padding-top: 4rem;
    }

    > .new-project-btn {
      display: none;
    }
  }

  .list-toolbar {
    grid-template-columns: 1fr;
  }

  .results-count,
  .toolbar-center {
    grid-column: 1;
  }

  .toolbar-center {
    justify-self: center;
    width: 100%;
  }
}

@media screen and (max-width: 725px) {
  .projects-page {
    top: 116px;
  }
}

@media screen and (max-width: 600px) {
  .projects-main {
    padding: 0.85rem;
  }
}
</style>

<style lang="less">
/* Widen the home page beyond the global .page-content max-width */
.page-content#projects,
#app-content > #projects.page-content {
  max-width: none;
}

html:has(#projects),
body:has(#projects) {
  height: 100%;
  overflow: hidden;
}

#app:has(#projects) {
  height: 100vh;
  height: 100dvh;
  max-height: 100dvh;
  min-height: 0;
  overflow: hidden;
}

#app:has(#projects) #app-header,
#app:has(#projects) #app-footer {
  flex: 0 0 auto;
}

#app:has(#projects) #app-content {
  min-height: 0;
  overflow: hidden;
  background: #f3f5f8;
}

#app #projects.page-content {
  position: fixed;
  top: 55px;
  right: 0;
  bottom: 40px;
  left: 0;
  margin: 0;
  max-width: none;
}

@media screen and (max-width: 725px) {
  #app #projects.page-content {
    top: 116px;
  }
}
</style>
